import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CommandMenu } from '@/components/layout/command-menu';
import { Sidebar } from '@/components/layout/sidebar';
import { Topbar } from '@/components/layout/topbar';
import { TooltipProvider } from '@/components/ui/primitives';
import { useAccountsStore } from '@/stores/accounts-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePostsStore } from '@/stores/posts-store';

/**
 * The authenticated shell: sidebar + topbar + the routed page.
 *
 * Also the one place that owns app-wide data loading, so that accounts are
 * fetched once on entry rather than by every page that happens to need them.
 */
export function AppShell() {
  const location = useLocation();
  const userId = useAuthStore((s) => s.user?.id);

  const fetchAccounts = useAccountsStore((s) => s.fetch);
  const subscribeToUpdates = usePostsStore((s) => s.subscribeToUpdates);

  React.useEffect(() => {
    void fetchAccounts();
  }, [fetchAccounts]);

  // Live queue updates for as long as the user is signed in. Subscribing here
  // rather than in the Queue page means a post that publishes while the user is
  // on the dashboard still updates the store — so the queue is already correct
  // when they navigate to it, with no loading flash.
  React.useEffect(() => {
    if (!userId) return;
    return subscribeToUpdates(userId);
  }, [userId, subscribeToUpdates]);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="bg-background flex h-dvh overflow-hidden">
        <Sidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />

          <main className="flex-1 overflow-y-auto">
            {/*
              Page transitions.

              `mode="wait"` so the outgoing page finishes leaving before the new
              one arrives — overlapping them causes a visible double-scrollbar
              flicker. The movement is tiny (4px) and fast (200ms): a page
              transition you *notice* is a page transition that is slowing you
              down, and this app is a tool, not a brochure.
            */}
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8"
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

        <CommandMenu />
      </div>
    </TooltipProvider>
  );
}
